import React, { useMemo } from "react";
import { Badge, Tooltip, theme } from "antd";
const { useToken } = theme;
import { hooks, models } from "@vote4dance/judging-sdk";
import { _T } from "../../base-react/src/Localization";
import { useParams } from "react-router-dom";
var Stream = hooks.streams.Stream;
const ACTIVE_WINDOW_MS = 45 * 1000;
const isActiveApplication = (item) => (Date.now() - getUpdateTimestampMs(item.update_at) < ACTIVE_WINDOW_MS);
const getActiveApplications = (applications, applicationName) => ((applications || [])
    .filter((item) => !applicationName || item.application_name === applicationName)
    .filter(isActiveApplication));
const normalizeBatteryLevel = (value) => {
    const numericValue = typeof value === "string" ? Number(value) : value;
    if (typeof numericValue !== "number" || !Number.isFinite(numericValue) || numericValue < 0) {
        return undefined;
    }
    if (numericValue <= 1) {
        return Math.round(numericValue * 100);
    }
    if (numericValue <= 100) {
        return Math.round(numericValue);
    }
    return undefined;
};
const getUpdateTimestampMs = (updateAt) => {
    if (typeof updateAt === "number") {
        return updateAt > 1000000000000 ? updateAt : updateAt * 1000;
    }
    const parsed = Date.parse(String(updateAt));
    return Number.isNaN(parsed) ? 0 : parsed;
};
export const getActiveApplicationUserIds = (applications, applicationName) => {
    return new Set(getActiveApplications(applications, applicationName)
        .map((item) => String(item.user_id)));
};
export const getActiveApplicationBatteryLevels = (applications, applicationName) => {
    const batteryLevels = new Map();
    getActiveApplications(applications, applicationName).forEach((item) => {
        const userId = String(item.user_id);
        const level = normalizeBatteryLevel(item.battery_level);
        if (!userId || level === undefined) {
            return;
        }
        const updateAt = getUpdateTimestampMs(item.update_at);
        const existing = batteryLevels.get(userId);
        if (!existing || updateAt >= existing.updateAt) {
            batteryLevels.set(userId, { level, updateAt });
        }
    });
    return new Map(Array.from(batteryLevels.entries()).map(([key, value]) => [key, value.level]));
};
export const getActiveScreenIds = (applications, competitionId) => {
    const prefix = `event/${competitionId}/`;
    return new Set(getActiveApplications(applications)
        .filter((item) => item.application_name === "screens")
        .map((item) => item.application_url)
        .filter((applicationUrl) => typeof applicationUrl === "string")
        .filter((applicationUrl) => applicationUrl.startsWith(prefix))
        .map((applicationUrl) => applicationUrl.slice(prefix.length).split("/")[0])
        .filter(Boolean));
};
export const getActiveScreenBatteryLevels = (applications, competitionId) => {
    const prefix = `event/${competitionId}/`;
    const batteryLevels = new Map();
    getActiveApplications(applications)
        .filter((item) => item.application_name === "screens")
        .forEach((item) => {
        const applicationUrl = typeof item.application_url === "string" ? item.application_url : "";
        if (!applicationUrl.startsWith(prefix)) {
            return;
        }
        const screenId = applicationUrl.slice(prefix.length).split("/")[0];
        const level = normalizeBatteryLevel(item.battery_level);
        if (!screenId || level === undefined) {
            return;
        }
        const updateAt = getUpdateTimestampMs(item.update_at);
        const existing = batteryLevels.get(screenId);
        if (!existing || updateAt >= existing.updateAt) {
            batteryLevels.set(screenId, { level, updateAt });
        }
    });
    return new Map(Array.from(batteryLevels.entries()).map(([key, value]) => [key, value.level]));
};
export const getApplicationStatusTitle = (connected, batteryLevel) => {
    if (!connected) {
        return _T("application.status.disconnected");
    }
    if (typeof batteryLevel === "number") {
        return _T("application.status.connected-battery", { level: batteryLevel });
    }
    return _T("application.status.connected");
};
export const ApplicationStatusDot = ({ connected, batteryLevel, applicationName, applicationUserId, screenId }) => {
    const { event_id } = useParams();
    const { token } = useToken();
    const [applications] = Stream(event_id ? models.applications.CompetitionApplicationsSource(event_id) : undefined);
    const resolvedBatteryLevel = useMemo(() => {
        if (typeof batteryLevel === "number") {
            return batteryLevel;
        }
        if (applicationUserId !== undefined) {
            return getActiveApplicationBatteryLevels(applications, applicationName).get(String(applicationUserId));
        }
        if (event_id !== undefined && screenId !== undefined) {
            return getActiveScreenBatteryLevels(applications, event_id).get(String(screenId));
        }
        return undefined;
    }, [batteryLevel, applications, applicationName, applicationUserId, event_id, screenId]);
    const title = useMemo(() => getApplicationStatusTitle(connected, resolvedBatteryLevel), [connected, resolvedBatteryLevel]);
    const hasBattery = typeof resolvedBatteryLevel === "number";
    const dotColor = !connected
        ? token.colorTextQuaternary
        : (hasBattery && resolvedBatteryLevel <= 20)
            ? token.colorError
            : token.colorSuccess;
    const w = 8, bodyH = 11, capH = 2;
    const fillH = hasBattery
        ? Math.round((resolvedBatteryLevel / 100) * (bodyH - 2))
        : (connected ? bodyH - 2 : 0);
    return (React.createElement("span", { style: { display: "flex", justifyContent: "center", alignItems: "center" } },
        React.createElement(Tooltip, { title: title }, hasBattery ? (React.createElement("svg", { width: w, height: bodyH + capH + 1, viewBox: `0 0 ${w} ${bodyH + capH + 1}`, style: { display: "block" } },
            React.createElement("rect", { x: 2, y: 0, width: w - 4, height: capH, rx: 1, fill: dotColor }),
            React.createElement("rect", { x: 0, y: capH, width: w, height: bodyH, rx: 1.5, fill: "none", stroke: dotColor, strokeWidth: 1 }),
            React.createElement("rect", { x: 1.5, y: capH + 1 + (bodyH - 2 - fillH), width: w - 3, height: Math.max(0, fillH), rx: 0.5, fill: dotColor }))) : (React.createElement(Badge, { status: connected ? "success" : "default" })))));
};
